<template>
    <div class="audit-trail">
        <!--
        #####################################################################################################
        # Screen version
        #####################################################################################################
        -->
        <div class="audit-trail-content no-print">
            <div class="filters-panel no-print">
                <div class="filters-wrapper">
                    <div class="filterby-wrapper">
                        <SelectBox
                            v-model="filterBy"
                            style-class="filterby-select-box"
                            :placeholder="$t('auditTrail.filterBy.filterBy')"
                            :options="filterByOptions"
                        />
                    </div>

                    <div class="date-range-wrapper">
                        <div class="date-range-type-wrapper">
                            <SelectBox
                                v-model="dateRange.type"
                                style-class="date-range-type-select-box audit-trail-show-on-narrow-screen"
                                :placeholder="$t('auditTrail.period')"
                                :options="dateRange.typeOptions"
                            />
                            <button
                                :class="[
                                    'btn',
                                    'time-range-button',
                                    'audit-trail-show-on-wide-screen',
                                    {
                                        active: dateRange.type === DATE_RANGE.DAY,
                                    },
                                ]"
                                @click="setDateRangeType(DATE_RANGE.DAY)"
                            >
                                {{ $t('auditTrail.dateRange.oneDay') }}
                            </button>
                            <button
                                :class="[
                                    'btn',
                                    'time-range-button',
                                    'audit-trail-show-on-wide-screen',
                                    {
                                        active: dateRange.type === DATE_RANGE.WEEK,
                                    },
                                ]"
                                @click="setDateRangeType(DATE_RANGE.WEEK)"
                            >
                                {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.WEEK }) }}
                            </button>
                            <button
                                :class="[
                                    'btn',
                                    'time-range-button',
                                    'audit-trail-show-on-wide-screen',
                                    {
                                        active: dateRange.type === DATE_RANGE.MONTH,
                                    },
                                ]"
                                @click="setDateRangeType(DATE_RANGE.MONTH)"
                            >
                                {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.MONTH }) }}
                            </button>
                            <button
                                :class="[
                                    'btn',
                                    'time-range-button',
                                    'audit-trail-show-on-wide-screen',
                                    {
                                        active: dateRange.type === DATE_RANGE.YEAR,
                                    },
                                ]"
                                @click="setDateRangeType(DATE_RANGE.YEAR)"
                            >
                                {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.YEAR }) }}
                            </button>
                            <div class="timepickers-wrapper">
                                <DateTimePickerForm ref="dateRangeForm" v-model="dateRange" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="table-panel no-print">
                <Loader :visible="isTableLoading" css-class="audit-trail-table-loader" />
                <div class="audit-trail-table">
                    <div v-if="auditTrailDataFiltered.length === 0" class="table-panel">
                        <div v-if="!isLoading && !isTableLoading" class="no-data">
                            {{ $t('auditTrail.noLoginSessionsFound') }}
                        </div>
                    </div>
                    <div v-if="auditTrailDataFiltered.length > 0" class="ahead">
                        <div class="arow">
                            <div class="subrow">
                                <div class="subcol subcol-user-name">
                                    {{ $t('auditTrail.table.header.userName') }}
                                </div>
                                <div class="subcol subcol-status">
                                    {{ $t('auditTrail.table.header.status') }}
                                </div>
                                <div class="subcol subcol-login-time">
                                    {{ $t('auditTrail.table.header.loginTime') }}
                                </div>
                                <div class="subcol subcol-logout-time">
                                    {{ $t('auditTrail.table.header.logoutTime') }}
                                </div>
                                <div class="subcol subcol-duration">
                                    {{ $t('auditTrail.table.header.duration') }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="auditTrailDataFiltered.length > 0" class="table-panel">
                        <div class="abody">
                            <div v-for="user in auditTrailDataFiltered" :key="user.user_id" class="arow">
                                <template v-for="(session, sessionIdx) in currentPageSessions(user)">
                                    <div
                                        :key="session.id"
                                        :class="[
                                            'subrow',
                                            {
                                                online: user.is_online,
                                                deleted: !user.user_exists,
                                            },
                                        ]"
                                        @click.prevent="sessionIdx === 0 && toggleUserExpand(user.user_id)"
                                    >
                                        <template v-if="sessionIdx === 0">
                                            <div class="subcol subcol-user-name" :title="user.first_name">
                                                {{ user.first_name }}
                                            </div>
                                            <div class="subcol subcol-status">{{ user.status }}</div>
                                        </template>
                                        <template v-else-if="sessionIdx === 1">
                                            <div
                                                class="subcol subcol-user-name login-name"
                                                :title="userLoginNameLine(user)"
                                            >
                                                <span>{{ userLoginNameLine(user) }}</span>
                                            </div>
                                            <div class="subcol subcol-status"></div>
                                        </template>
                                        <template v-else>
                                            <div class="subcol subcol-user-name"></div>
                                            <div class="subcol subcol-status"></div>
                                        </template>
                                        <div class="subcol subcol-login-time">
                                            {{ session.loginTimeFormatted }}
                                        </div>
                                        <div class="subcol subcol-logout-time">
                                            {{ session.logoutTimeFormatted }}
                                        </div>
                                        <div class="subcol subcol-duration">{{ session.duration }}</div>
                                        <div class="subcol subcol-chewron">
                                            <div
                                                v-if="sessionIdx === 0"
                                                :class="['chewron', { expanded: user.isExpanded }]"
                                            >
                                                <i class="icon-tab-show"></i>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-if="user.isExpanded && user.sessions.length === 1">
                                    <div class="subrow">
                                        <div
                                            class="subcol subcol-user-name login-name"
                                            :title="userLoginNameLine(user)"
                                        >
                                            <span>{{ userLoginNameLine(user) }}</span>
                                        </div>
                                    </div>
                                </template>
                                <template v-if="user.isExpanded && user.sessions.length > 10">
                                    <div class="subrow pagination-wrapper">
                                        <Pagination
                                            v-model="user.pagination.current_page"
                                            :options="user.pagination"
                                            :callback="() => {}"
                                        />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--
        #####################################################################################################
        # Print version
        #####################################################################################################
        -->
        <div class="audit-trail-content print-only">
            <div v-if="isLoading || isTableLoading" class="no-data">{{ $t('common.dataIsLoading') }}</div>
            <div v-else>
                <div class="filters-panel">
                    <table>
                        <tr>
                            <td>{{ $t('auditTrail.users') }}</td>
                            <td>{{ filteredUsers }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('auditTrail.filterBy.filterBy') }}</td>
                            <td>{{ filterByOptionName }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('auditTrail.searchBy') }}</td>
                            <td>{{ searchByKeyword }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('auditTrail.period') }}</td>
                            <td>{{ dateRangePeriodPrinted }}</td>
                        </tr>
                    </table>
                </div>
                <div class="audit-trail-table">
                    <div v-if="auditTrailDataFiltered.length === 0" class="table-panel">
                        <div class="no-data">{{ $t('auditTrail.noLoginSessionsFound') }}</div>
                    </div>
                    <table v-else class="common-table">
                        <thead>
                            <tr>
                                <th class="col-user-name">{{ $t('auditTrail.table.header.userName') }}</th>
                                <th class="col-login-time">{{ $t('auditTrail.table.header.loginTime') }}</th>
                                <th class="col-logout-time">
                                    {{ $t('auditTrail.table.header.logoutTime') }}
                                </th>
                                <th class="col-duration">{{ $t('auditTrail.table.header.duration') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="user in auditTrailDataFiltered">
                                <template v-for="(session, sessionIdx) in user.sessions">
                                    <tr :key="session.id" :class="{ first: sessionIdx === 0 }">
                                        <template v-if="sessionIdx === 0">
                                            <td class="col-user-name">{{ user.first_name }}</td>
                                        </template>
                                        <template v-else-if="sessionIdx === 1">
                                            <td class="col-user-name login-name">
                                                {{ userLoginNameLine(user) }}
                                            </td>
                                        </template>
                                        <template v-else>
                                            <td class="col-user-name"></td>
                                        </template>
                                        <td class="col-login-time">{{ session.loginTimeFormatted }}</td>
                                        <td class="col-logout-time">{{ session.logoutTimeFormatted }}</td>
                                        <td class="col-duration">{{ session.duration }}</td>
                                    </tr>
                                </template>
                                <template v-if="user.sessions.length === 1">
                                    <tr :key="user.user_id">
                                        <td class="col-user-name login-name">
                                            {{ userLoginNameLine(user) }}
                                        </td>
                                        <td class="col-login-time"></td>
                                        <td class="col-logout-time"></td>
                                        <td class="col-duration"></td>
                                    </tr>
                                </template>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex';

import differenceInSeconds from 'date-fns/differenceInSeconds';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import isToday from 'date-fns/isToday';
import orderBy from 'lodash-es/orderBy';
import parseISO from 'date-fns/parseISO';
import set from 'date-fns/set';
import subDays from 'date-fns/subDays';

import DateTimePickerForm from '../common/datetimepicker/DateTimePickerForm.vue';
import Loader from '../common/Loader.vue';
import Pagination from '../common/Pagination.vue';
import SelectBox from '../common/SelectBox';
import mappingState from '../common/mappingState';

import { ifIsNotCanceled, getFullDateFormat, getDateFormat } from '../common/helpers';

import { getCancelToken } from '../../api';
import { resourceMariAuditTrail } from '../../api/resources';
import { AUDIT_TRAIL, DEF_DATE_FORMAT, ROUTE_NAMES } from '../../constants';

const DATE_RANGE_TYPES = Object.values(AUDIT_TRAIL.DATE_RANGE);

export default {
    name: 'LogIn',

    components: {
        DateTimePickerForm,
        Loader,
        Pagination,
        SelectBox,
    },

    mixins: [mappingState],

    constants: {
        DATE_RANGE: AUDIT_TRAIL.DATE_RANGE,
    },

    data() {
        return {
            filterBy: AUDIT_TRAIL.FILTER_BY.ALL,
            filterByOptions: [
                { value: AUDIT_TRAIL.FILTER_BY.ALL, name: this.$t('auditTrail.filterBy.allUsers') },
                { value: AUDIT_TRAIL.FILTER_BY.ONLINE, name: this.$t('auditTrail.filterBy.usersOnline') },
                { value: AUDIT_TRAIL.FILTER_BY.OFFLINE, name: this.$t('auditTrail.filterBy.usersOffline') },
                { value: AUDIT_TRAIL.FILTER_BY.EXISTING, name: this.$t('auditTrail.filterBy.existingUsers') },
                { value: AUDIT_TRAIL.FILTER_BY.DELETED, name: this.$t('auditTrail.filterBy.deletedUsers') },
            ],
            dateRange: {
                type: AUDIT_TRAIL.DATE_RANGE.WEEK,
                typeOptions: [
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.DAY,
                        name: this.$t('auditTrail.dateRange.oneDay'),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.WEEK,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.WEEK }),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.MONTH,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.MONTH }),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.YEAR,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.YEAR }),
                    },
                ],
                from: {
                    dateSaved: null,
                    date: set(subDays(new Date(), AUDIT_TRAIL.DATE_RANGE.WEEK - 1), {
                        hours: 0,
                        minutes: 0,
                        seconds: 0,
                    }),
                },
                to: {
                    dateSaved: null,
                    date: set(new Date(), { hours: 23, minutes: 59, seconds: 0 }),
                },
                shortFormat: true,
            },
            auditTrailData: [],
            isTableLoading: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['isLoading']),

        /**
         * Check if date range is valid
         */
        dateRangeFormValid() {
            return (
                !this.$refs.dateRangeForm.$v.dateFromFormatted.$invalid &&
                !this.$refs.dateRangeForm.$v.dateToFormatted.$invalid
            );
        },

        /**
         * Audit trail data,
         *   searched by keyword
         *   and filtered accordingly filterBy.
         */
        auditTrailDataFiltered() {
            return this.auditTrailData.filter(this.userFilterBySearchKeyword).filter(this.userFilterByStatus);
        },

        /**
         * List of filtered user names
         */
        filteredUsers() {
            return (
                this.auditTrailDataFiltered.map(user => user.first_name).join(', ') ||
                this.$t('auditTrail.noUsersFound')
            );
        },

        /**
         * Keyword of search for print
         */
        searchByKeyword() {
            return this.searchKeyword.length > 0 ? this.searchKeyword : '-';
        },
        /**
         * Current filterBy option name
         */
        filterByOptionName() {
            return this.filterByOptions.find(item => item.value === this.filterBy).name;
        },

        /**
         * Get selected date range for print version
         */
        dateRangePeriodPrinted() {
            return this.dateRange.from.dateSaved && this.dateRange.to.dateSaved
                ? this.$t('auditTrail.selectedTime', {
                      from: format(this.dateRange.from.dateSaved, getDateFormat()),
                      to: format(this.dateRange.to.dateSaved, getDateFormat()),
                  })
                : this.dateRange.from.dateSaved
                ? this.$t('auditTrail.startTime', {
                      from: format(this.dateRange.from.dateSaved, getDateFormat()),
                  })
                : this.dateRange.to.dateSaved
                ? this.$t('auditTrail.endTime', {
                      to: format(this.dateRange.to.dateSaved, getDateFormat()),
                  })
                : this.$t('auditTrail.fullPeriod');
        },
    },

    watch: {
        'dateRange.from.date'() {
            this.$nextTick(() => {
                // Wait for latest validation update in DateTimePickerForm subcomponent
                if (this.dateRangeFormValid) {
                    this.loadDataWithLoader();
                    this.setDateRangeTypeByDates();
                }
            });
        },
        'dateRange.to.date'() {
            this.$nextTick(() => {
                // Wait for latest validation update in DateTimePickerForm subcomponent
                if (this.dateRangeFormValid) {
                    this.loadDataWithLoader();
                    this.setDateRangeTypeByDates();
                }
            });
        },
        'dateRange.type'(dateRangeType) {
            if (DATE_RANGE_TYPES.includes(dateRangeType)) {
                let today = new Date();
                this.dateRange.from.date = set(subDays(today, dateRangeType - 1), {
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                this.dateRange.to.date = set(today, { hours: 23, minutes: 59, seconds: 0 });
                this.$refs.dateRangeForm.updateDateFields();
            }
        },
    },

    created() {
        this.setSearchEnabled(true);
        this.showLoading(true);
        return this.loadDataWithLoader()
            .then(() => {
                if (this.$router.currentRoute.name === ROUTE_NAMES.AUDIT_TRAIL__LOG_IN) {
                    this.showLoading(false);
                }
            })
            .catch(response => {
                ifIsNotCanceled(response);
            });
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * User filter by search keyword
         * @param {Object} user
         * @returns {Boolean} true if user should be included to filtered array
         */
        userFilterBySearchKeyword(user) {
            return (
                !this.searchKeyword ||
                user.first_name.toLowerCase().includes(this.searchKeyword) ||
                user.login_name.toLowerCase().includes(this.searchKeyword)
            );
        },

        /**
         * User filter by status
         * @param {Object} user
         * @returns {Boolean} true if user should be included to filtered array
         */
        userFilterByStatus(user) {
            return (
                this.filterBy === AUDIT_TRAIL.FILTER_BY.ALL ||
                (this.filterBy === AUDIT_TRAIL.FILTER_BY.ONLINE && user.is_online) ||
                (this.filterBy === AUDIT_TRAIL.FILTER_BY.OFFLINE && !user.is_online && user.user_exists) ||
                (this.filterBy === AUDIT_TRAIL.FILTER_BY.EXISTING && user.user_exists) ||
                (this.filterBy === AUDIT_TRAIL.FILTER_BY.DELETED && !user.user_exists)
            );
        },

        /**
         * Get time difference in format hhh:mm:ss, where hhh can be more than 24 hours,
         *   if timeStart and timeFinish present.
         * Return difference between timeStart and current server time, if this is the last session online.
         * Return 'undefined' otherwise.
         *
         * But in case of last session online, return '-'.
         * Return formatted time, if time is given.
         * Return '-', if this is last session online.
         * Return 'unknown' otherwise.
         *
         * @param {String} timeStart
         * @param {String} timeFinish
         * @param {String} timeNow current time on server
         * @param {Boolean} isLastSessionAndOnline
         * @returns {String} time formatted
         */
        timeDifference(timeStart, timeFinish, timeNow, isLastSessionAndOnline) {
            let timeEnd = isLastSessionAndOnline ? timeNow : timeFinish;
            if (!timeStart || !timeEnd) {
                return this.$t('auditTrail.table.content.unknown');
            }

            let s = differenceInSeconds(parseISO(timeEnd), parseISO(timeStart));

            // Break s into hours:minutes:seconds
            let h = Math.floor(s / 3600);
            s = s % 3600;
            let m = Math.floor(s / 60);
            s = s % 60;

            return [h, m, s].map(item => String(item).padStart(2, '0')).join(':');
        },

        /**
         * Return formatted time, if time is given.
         * Return '-', if this is the last session online.
         * Return 'unknown' otherwise.
         *
         * @param {String} time
         * @param {Boolean} isLastSessionAndOnline
         * @returns {String} time formatted
         */
        actionTimeFormatted(time, isLastSessionAndOnline) {
            if (!time) {
                return isLastSessionAndOnline ? '-' : this.$t('auditTrail.table.content.unknown');
            }
            return format(parseISO(time), getFullDateFormat());
        },

        /**
         * Show/hide table loader
         * @param {Boolean} isTableLoading
         */
        showTableLoading(isTableLoading) {
            if (!this.isLoading) {
                // do not show table loader during page loading
                this.isTableLoading = isTableLoading;
            }
        },

        /**
         * Load audit trail data, and show loader during the process
         * @returns {Promise}
         */
        loadDataWithLoader() {
            this.showTableLoading(true);
            return this.loadData()
                .then(() => {
                    this.showTableLoading(false);
                })
                .catch(response => {
                    ifIsNotCanceled(response);
                });
        },

        /**
         * Save last correct date range
         */
        saveLastCorrectDateRange() {
            this.dateRange.from.dateSaved = this.dateRange.from.date || null;
            this.dateRange.to.dateSaved = this.dateRange.to.date || null;
        },

        /**
         * Get user status text
         */
        getUserStatusText(userExists, isUserOnline) {
            if (!userExists) {
                return this.$t('auditTrail.deleted');
            }
            return isUserOnline
                ? this.$t('auditTrail.table.content.online')
                : this.$t('auditTrail.table.content.offline');
        },

        /**
         * Load audit trail data
         * @returns {Promise}
         */
        loadData() {
            // Cancel previous request
            this.cancelToken.cancel();
            this.cancelToken = getCancelToken();

            this.saveLastCorrectDateRange();
            let expandedUserId = this.findExpandedUserId();
            return resourceMariAuditTrail({
                cancelToken: this.cancelToken.token,
                params: {
                    time_from: this.dateRange.from.date
                        ? format(
                              set(this.dateRange.from.date, { hours: 0, minutes: 0, seconds: 0 }),
                              DEF_DATE_FORMAT
                          )
                        : null,
                    time_to: this.dateRange.to.date
                        ? format(
                              set(this.dateRange.to.date, { hours: 23, minutes: 59, seconds: 0 }),
                              DEF_DATE_FORMAT
                          )
                        : null,
                },
            })
                .then(response => {
                    let at = Object.values(response.data.audit_trail).map(user => ({
                        ...user,
                        status: this.getUserStatusText(user.user_exists, user.is_online),
                        isExpanded: false,
                        sessions: user.sessions.map(item => ({
                            ...item,
                            duration: this.timeDifference(
                                item.login_time,
                                item.logout_time,
                                response.data.now,
                                item.session_id === user.last_session_id && user.is_online
                            ),
                            loginTimeFormatted: this.actionTimeFormatted(item.login_time),
                            logoutTimeFormatted: this.actionTimeFormatted(
                                item.logout_time,
                                item.session_id === user.last_session_id && user.is_online
                            ),
                        })),
                        pagination: {
                            current_page: 1,
                            last_page: Math.ceil(user.sessions.length / AUDIT_TRAIL.PAGE_SIZE),
                            per_page: AUDIT_TRAIL.PAGE_SIZE,
                        },
                    }));
                    this.auditTrailData = orderBy(at, 'first_name');
                    // Expanded user remains expanded after data reload
                    this.expandUser(expandedUserId);
                })
                .catch(error => {
                    this.auditTrailData = [];
                    return Promise.reject(error);
                });
        },

        /**
         * Find expanded user id
         * @return {Number} user_id of the expanded user or undefined if not found
         */
        findExpandedUserId() {
            let userExpanded = this.auditTrailData.find(user => user.isExpanded);
            return userExpanded !== undefined ? userExpanded.user_id : null;
        },

        /**
         * Expand user by user_id
         *    and collapse other users.
         * It is done after data reload, when the expanded user current session page was reset to page 1.
         *
         * @param {Number} user_id id of the user to expand
         */
        expandUser(user_id) {
            this.auditTrailData = this.auditTrailData.map(user => ({
                ...user,
                isExpanded: user.user_id === user_id,
            }));
        },

        /**
         * Toggle user with id user_id,
         *   and collapse other users
         * @param {Number} user_id
         */
        toggleUserExpand(user_id) {
            this.auditTrailData = this.auditTrailData.map(user => ({
                ...user,
                isExpanded: user.user_id === user_id && !user.isExpanded,
                pagination: {
                    ...user.pagination,
                    current_page: 1, // Toggling the user redirects to its sessions page 1, in both cases:
                    // 1) just colapsed user shows only the last session, namely session[0] from page 1;
                    // 2) just expanded user shows its sessions page 1.
                },
            }));
        },

        /**
         * Return current page sessions of the user
         * @param {Object} user
         */
        currentPageSessions(user) {
            if (!user.isExpanded) {
                return [user.sessions[0]];
            }
            if (user.sessions.length <= AUDIT_TRAIL.PAGE_SIZE) {
                return user.sessions;
            }

            // Here if there are more than one page
            let sessionsToShow = user.sessions.slice(
                (user.pagination.current_page - 1) * AUDIT_TRAIL.PAGE_SIZE,
                user.pagination.current_page * AUDIT_TRAIL.PAGE_SIZE
            );
            let emptySession = {
                loginTimeFormatted: '',
                logoutTimeFormatted: '',
                duration: '',
            };

            // Fill the page with empty sessions if needed,
            // so that even the last page will have same height as other pages
            for (let i = sessionsToShow.length; i < AUDIT_TRAIL.PAGE_SIZE; i++) {
                sessionsToShow.push(emptySession);
            }
            return sessionsToShow;
        },

        /**
         * Set date range type using buttons
         * @param {Number} dateRangeType
         */
        setDateRangeType(dateRangeType) {
            if (this.dateRange.type !== dateRangeType) {
                this.dateRange.type = dateRangeType;
                let today = new Date();
                this.dateRange.from.date = set(subDays(today, dateRangeType - 1), {
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                this.dateRange.to.date = set(today, { hours: 23, minutes: 59, seconds: 0 });
            }
        },

        /**
         * Set date range type using set dates
         */
        setDateRangeTypeByDates() {
            let today = new Date();
            this.dateRange.type = null;

            if (isToday(this.dateRange.to.date)) {
                DATE_RANGE_TYPES.forEach(dateRangeType => {
                    let calculatedDate = subDays(today, dateRangeType - 1);
                    if (isSameDay(this.dateRange.from.date, calculatedDate)) {
                        this.dateRange.type = dateRangeType;
                    }
                });
            }
        },

        /**
         * User login in format:
         *      Login: mmm
         * @param {Object} user
         */
        userLoginNameLine(user) {
            return this.$t('auditTrail.login') + ': ' + user.login_name;
        },
    },
};
</script>
